<template>
  <div class="task-comments">
    <div class="task-comments__header">
      <h3 class="task-comments__title">Comments ({{ comments.length }})</h3>
    </div>

    <!-- New Comment Form (Moved to top) -->
    <div class="task-comments__form">
      <div class="task-comments__form-label">
        <span>Add a comment</span>
      </div>
      <themed-rich-text-editor
        v-model="newComment"
        preset="comment"
        placeholder="Type @ to mention users..."
        @text-change="handleTextChange"
      />
      <div class="task-comments__form-actions">
        <themed-button
          @click="addComment"
          :disabled="!newComment.trim() || submitting"
          size="sm"
        >
          <span v-if="submitting">Posting...</span>
          <span v-else>Add Comment</span>
        </themed-button>
      </div>
    </div>

    <!-- Comment List -->
    <div class="task-comments__list" v-if="comments.length">
      <div
        v-for="comment in comments"
        :key="comment.id"
        class="task-comments__item"
      >
        <div class="task-comments__item-header">
          <span class="task-comments__author">{{ comment.user ? comment.user.name : 'Unknown' }}</span>
          <span class="task-comments__date">{{ formatDate(comment.created_at) }}</span>
          <div class="task-comments__item-actions" v-if="canModifyComment(comment)">
            <themed-button
              v-if="comment.user_id === currentUserId"
              variant="ghost"
              size="sm"
              @click="startEditing(comment)"
              aria-label="Edit comment"
            >
              <theme-icon icon="edit" type="icons" classes="w-3 h-3" />
            </themed-button>
            <themed-button
              variant="ghost"
              size="sm"
              @click="deleteComment(comment)"
              aria-label="Delete comment"
            >
              <theme-icon icon="trash" type="icons" classes="w-3 h-3" />
            </themed-button>
          </div>
        </div>

        <!-- Edit Mode -->
        <div v-if="editingCommentId === comment.id" class="task-comments__edit">
          <themed-rich-text-editor
            v-model="editBody"
            preset="comment"
            placeholder="Edit your comment..."
          />
          <div class="task-comments__edit-actions">
            <themed-button variant="secondary" size="sm" @click="cancelEditing">Cancel</themed-button>
            <themed-button size="sm" @click="updateComment(comment)" :disabled="!editBody.trim()">Save</themed-button>
          </div>
        </div>

        <!-- Display Mode -->
        <div v-else class="task-comments__body" v-html="comment.body"></div>
      </div>
    </div>

    <div v-else class="task-comments__empty">No comments yet.</div>
  </div>
</template>

<script>
import ThemedRichTextEditor from '../form/ThemedRichTextEditor.vue';
import ThemedButton from '../ThemedButton.vue';
import ThemeIcon from '../ThemeIcon.vue';

export default {
  name: 'TaskComments',

  components: {
    ThemedRichTextEditor,
    ThemedButton,
    ThemeIcon
  },

  props: {
    taskId: {
      type: [Number, String],
      required: true
    }
  },

  data() {
    return {
      comments: [],
      newComment: '',
      submitting: false,
      editingCommentId: null,
      editBody: '',
      availableUsers: []
    };
  },

  computed: {
    currentUserId() {
      return window.user?.id || null;
    },
    isClientAdmin() {
      return window.user?.is_client_admin || false;
    }
  },

  watch: {
    taskId: {
      immediate: true,
      handler(newId) {
        if (newId) {
          this.fetchComments();
          this.fetchUsers();
        }
      }
    }
  },

  methods: {

    async fetchUsers() {
      try {
        const response = await axios.get('/api/users', {
          params: { per_page: 100 }
        });
        this.availableUsers = (response.data.data || response.data).map(user => ({
          id: user.id,
          value: user.name,
          email: user.email
        }));
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    },

    handleTextChange(delta, oldDelta, source) {
      // Handle text changes if needed for future enhancements
    },

    async fetchComments() {
      try {
        const response = await axios.get(`/api/tasks/${this.taskId}/comments`);
        this.comments = response.data.data || [];
      } catch (error) {
        console.error('Error fetching comments:', error);
      }
    },

    async addComment() {
      if (!this.newComment.trim()) return;
      this.submitting = true;

      try {
        // Extract mentioned user IDs from the HTML content
        const mentionedUserIds = this.extractMentionedUsers(this.newComment);
        
        const response = await axios.post(`/api/tasks/${this.taskId}/comments`, {
          body: this.newComment,
          mentioned_user_ids: mentionedUserIds
        });
        this.comments.push(response.data);
        this.newComment = '';
        this.$emit('comment-added', response.data);
        
        this.$notify({
          type: 'success',
          title: 'Success',
          text: 'Comment added successfully.'
        });
      } catch (error) {
        console.error('Error adding comment:', error);
        this.$notify({
          type: 'error',
          title: 'Error',
          text: error.response?.data?.message || 'Failed to add comment.'
        });
      } finally {
        this.submitting = false;
      }
    },

    extractMentionedUsers(htmlContent) {
      // Extract user IDs from mention spans (format: <span class="mention" data-id="123">@User Name</span>)
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlContent, 'text/html');
      const mentions = doc.querySelectorAll('.mention[data-id]');
      return Array.from(mentions).map(el => parseInt(el.getAttribute('data-id'))).filter(id => !isNaN(id));
    },

    startEditing(comment) {
      this.editingCommentId = comment.id;
      this.editBody = comment.body;
    },

    cancelEditing() {
      this.editingCommentId = null;
      this.editBody = '';
    },

    async updateComment(comment) {
      if (!this.editBody.trim()) return;

      try {
        const mentionedUserIds = this.extractMentionedUsers(this.editBody);
        
        const response = await axios.put(
          `/api/tasks/${this.taskId}/comments/${comment.id}`,
          { 
            body: this.editBody,
            mentioned_user_ids: mentionedUserIds
          }
        );
        const index = this.comments.findIndex(c => c.id === comment.id);
        if (index !== -1) {
          this.$set(this.comments, index, response.data);
        }
        this.cancelEditing();
        
        this.$notify({
          type: 'success',
          title: 'Success',
          text: 'Comment updated successfully.'
        });
      } catch (error) {
        console.error('Error updating comment:', error);
        this.$notify({
          type: 'error',
          title: 'Error',
          text: error.response?.data?.message || 'Failed to update comment.'
        });
      }
    },

    async deleteComment(comment) {
      if (!confirm('Are you sure you want to delete this comment?')) return;

      try {
        await axios.delete(`/api/tasks/${this.taskId}/comments/${comment.id}`);
        this.comments = this.comments.filter(c => c.id !== comment.id);
        this.$emit('comment-deleted', comment.id);
        
        this.$notify({
          type: 'success',
          title: 'Success',
          text: 'Comment deleted successfully.'
        });
      } catch (error) {
        console.error('Error deleting comment:', error);
        this.$notify({
          type: 'error',
          title: 'Error',
          text: error.response?.data?.message || 'Failed to delete comment.'
        });
      }
    },

    canModifyComment(comment) {
      return comment.user_id === this.currentUserId || this.isClientAdmin;
    },

    formatDate(dateStr) {
      if (!dateStr) return '';
      return new Date(dateStr).toLocaleString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric',
        hour: 'numeric', minute: '2-digit'
      });
    }
  }
};
</script>
